/**
 * ReauthBanner — Cluster A (Feb 2026), bilingual in Cluster C-1 (Feb 06 2026).
 *
 * Inline, persistent banner pinned at the top of the chat layout
 * whenever the central `apiFetch` wrapper has detected that the
 * session is no longer valid. Listens for `bayenah:reauth-needed`,
 * collects the user's password, calls `supabase.auth.signInWithPassword`,
 * and on success fires `bayenah:reauth-succeeded` so apiFetch can
 * drain its queue.
 *
 * Enterprise UX choice (per Cluster A decisions):
 *   • not a toast (too easy to miss)
 *   • not a modal (breaks flow on transient issues)
 *   • inline persistent — visible until resolved or cancelled
 *
 * The banner never wipes localStorage on its own; it only triggers
 * a fresh sign-in. The decision to actually clear state lives in
 * AuthContext's existing SIGNED_OUT handler.
 *
 * Cluster C-1: every visible string now sources from `useLanguage`.
 * Form direction follows `language === 'ar'` so the password input
 * sits on the correct side under RTL.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/lib/supabase';

export const ReauthBanner = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const [visible, setVisible] = useState(false);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Centralized copy table — keeps the JSX clean and the translation
  // surface auditable in one place. Any new visible string in this
  // component MUST be added here.
  const T = isArabic
    ? {
      title: 'انتهت جلستك.',
      body: 'سجّل الدخول مرة أخرى للمتابعة — ستُستأنف العمليات المعلّقة تلقائياً.',
      passwordPlaceholder: 'كلمة المرور',
      submit: 'تسجيل الدخول',
      cancel: 'إلغاء',
      // Cluster C-2 micro-fix (Feb 06 2026): AR/EN information parity.
      // Previously this said only "تعذّر تسجيل الدخول. يرجى المحاولة مرة أخرى."
      // which dropped a layer of actionable signal compared to the EN
      // path (which surfaces raw Supabase messages). Now mentions the
      // two most common causes explicitly.
      genericError: 'تعذّر تسجيل الدخول. تحقق من كلمة المرور أو اتصالك بالإنترنت ثم أعد المحاولة.',
    }
    : {
      title: 'Your session expired.',
      body: 'Sign in again to continue — your pending actions will resume automatically.',
      passwordPlaceholder: 'Password',
      submit: 'Sign in',
      cancel: 'Cancel',
      genericError: 'Sign-in failed. Please try again.',
    };

  useEffect(() => {
    const onNeed = () => setVisible(true);
    const onDone = () => setVisible(false);
    window.addEventListener('bayenah:reauth-needed', onNeed);
    window.addEventListener('bayenah:reauth-succeeded', onDone);
    window.addEventListener('bayenah:reauth-cancelled', onDone);
    return () => {
      window.removeEventListener('bayenah:reauth-needed', onNeed);
      window.removeEventListener('bayenah:reauth-succeeded', onDone);
      window.removeEventListener('bayenah:reauth-cancelled', onDone);
    };
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!user?.email) return;
    setSubmitting(true);
    setError('');
    try {
      const { data, error: signinErr } = await supabase.auth.signInWithPassword({
        email: user.email,
        password,
      });
      if (signinErr) throw signinErr;
      const newToken = data?.session?.access_token || null;
      window.dispatchEvent(new CustomEvent('bayenah:reauth-succeeded', {
        detail: { accessToken: newToken },
      }));
      setPassword('');
    } catch (err) {
      // Supabase returns provider-native error strings (usually English).
      // We surface them verbatim in EN; in AR we fall back to a
      // localized generic message because translating ad-hoc strings
      // at the boundary is unreliable.
      const raw = err?.message || '';
      setError(isArabic ? T.genericError : (raw || T.genericError));
    } finally {
      setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, password, isArabic]);

  const handleCancel = useCallback(() => {
    window.dispatchEvent(new CustomEvent('bayenah:reauth-cancelled'));
    setPassword('');
    setError('');
  }, []);

  if (!visible) return null;

  return (
    <div
      role="alert"
      dir={isArabic ? 'rtl' : 'ltr'}
      data-testid="reauth-banner"
      className="w-full px-4 py-3 flex flex-wrap items-center gap-3"
      style={{
        background: '#1A1A1A',
        borderBottom: '1px solid #2A2A2A',
        color: '#fff',
      }}
    >
      <ShieldAlert size={16} className="text-amber-400 flex-shrink-0" />
      <div className={`flex-1 min-w-[200px] text-sm ${isArabic ? 'font-arabic text-right' : ''}`}>
        <span className="font-medium" data-testid="reauth-banner-title">{T.title}</span>{' '}
        <span className="text-white/70" data-testid="reauth-banner-body">{T.body}</span>
      </div>
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <input
          type="email"
          value={user?.email || ''}
          readOnly
          className="h-8 px-2 text-xs rounded-md bg-black/50 border border-white/10 text-white/80 w-44"
          data-testid="reauth-banner-email"
        />
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={T.passwordPlaceholder}
          className={`h-8 px-2 text-xs rounded-md bg-black/50 border border-white/10 text-white w-40 focus:border-white/30 outline-none ${isArabic ? 'text-right font-arabic' : ''}`}
          dir={isArabic ? 'rtl' : 'ltr'}
          data-testid="reauth-banner-password"
        />
        <button
          type="submit"
          disabled={submitting || !password}
          className={`h-8 px-3 text-xs rounded-md bg-white text-black font-medium disabled:opacity-50 flex items-center gap-1 ${isArabic ? 'font-arabic' : ''}`}
          data-testid="reauth-banner-submit"
        >
          {submitting ? <Loader2 size={11} className="animate-spin" /> : null}
          {T.submit}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className={`h-8 px-2 text-xs text-white/60 hover:text-white ${isArabic ? 'font-arabic' : ''}`}
          data-testid="reauth-banner-cancel"
        >
          {T.cancel}
        </button>
      </form>
      {error ? (
        <div className={`w-full text-xs text-red-400 ${isArabic ? 'font-arabic text-right' : ''}`} data-testid="reauth-banner-error">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default ReauthBanner;
